/**
 * Step 1 of the Add User wizard: who the user is.
 */
import { DEPARTMENTS } from '../../lib/departments';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Mail, Phone, Briefcase } from 'lucide-react';
import { SALUTATIONS, type ProfileFields } from './constants';

interface ProfileStepProps {
  profile: ProfileFields;
  onChange: (next: ProfileFields) => void;
  /** Department picks go through here so the parent can pre-fill notification switches. */
  onDepartmentChange: (department: string) => void;
  /** Shown under the department picker when it pre-fills switches (customer invites). */
  departmentHint?: string;
}

export function ProfileStep({ profile, onChange, onDepartmentChange, departmentHint }: ProfileStepProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>1. User profile</CardTitle>
        <CardDescription>Who is this user? Company assignment comes next.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label>Title</Label>
            <Select value={profile.salutation || 'none'}
              onValueChange={v => onChange({ ...profile, salutation: v === 'none' ? '' : v })}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {SALUTATIONS.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 md:col-span-1">
            <Label htmlFor="first_name">First name *</Label>
            <Input id="first_name" required value={profile.first_name}
              onChange={e => onChange({ ...profile, first_name: e.target.value })} />
          </div>
          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="last_name">Last name *</Label>
            <Input id="last_name" required value={profile.last_name}
              onChange={e => onChange({ ...profile, last_name: e.target.value })} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="flex items-center gap-2"><Mail className="h-3.5 w-3.5" />Email *</Label>
            <Input id="email" type="email" required value={profile.email}
              onChange={e => onChange({ ...profile, email: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone" className="flex items-center gap-2"><Phone className="h-3.5 w-3.5" />Telephone</Label>
            <Input id="phone" value={profile.phone}
              onChange={e => onChange({ ...profile, phone: e.target.value })} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="job_title" className="flex items-center gap-2"><Briefcase className="h-3.5 w-3.5" />Job title</Label>
            <Input id="job_title" value={profile.job_title}
              onChange={e => onChange({ ...profile, job_title: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="department">Department</Label>
            <Select value={profile.department || 'none'}
              onValueChange={v => onDepartmentChange(v === 'none' ? '' : v)}>
              <SelectTrigger id="department"><SelectValue placeholder="Select department" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">—</SelectItem>
                {DEPARTMENTS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
              </SelectContent>
            </Select>
            {departmentHint && <p className="text-xs text-muted-foreground">{departmentHint}</p>}
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="message">Welcome message (optional)</Label>
          <Textarea id="message" rows={3} value={profile.message}
            onChange={e => onChange({ ...profile, message: e.target.value })}
            placeholder="Add a personal note for the invitee…" />
        </div>
      </CardContent>
    </Card>
  );
}
